import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Dropdown, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ReloadOutlined, StarFilled, SettingOutlined } from '@ant-design/icons';
import { taskAccess } from '../../../services/task_access';
import { useSocket } from '../../../utils/socketio/SocketWrapper';

interface AccessEntry {
  user_id: string;
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  is_owner: number;
  protected_access?: number;
  is_manager?: number;
}
const statusLabels = { pending: 'รออนุมัติ', approved: 'อนุมัติแล้ว', rejected: 'ปฏิเสธ / ถอนสิทธิ์แล้ว' };
const statusColors = { pending: 'gold', approved: 'green', rejected: 'red' };

export default function TabAccess({ taskId, canDelegate = false }: { taskId: string; canDelegate?: boolean }) {
  const { socket, isConnected } = useSocket() as any;
  const [entries, setEntries] = useState<AccessEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [delegateTarget, setDelegateTarget] = useState<AccessEntry | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await taskAccess('requests', { task_id: taskId });
      setEntries((result.requests || []).filter((entry: AccessEntry) => !entry.is_owner && entry.status !== 'rejected'));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const connection = socket?.current;
    if (!connection || !isConnected) return;
    const update = (event: { task_id: string }) => { if (event.task_id === taskId) load(); };
    connection.on('notification', update);
    return () => { connection.off('notification', update); };
  }, [socket, isConnected, taskId, load]);

  const decide = async (entry: AccessEntry, status: 'approved' | 'rejected') => {
    setBusyUser(entry.user_id);
    try {
      await taskAccess('decide', { task_id: taskId, user_id: entry.user_id, status });
      await load();
      message.success(status === 'approved' ? 'อนุมัติสิทธิ์แล้ว ผู้ใช้ต้องกด Register เพื่อเข้าร่วม' : 'ปฏิเสธ / ถอนสิทธิ์แล้ว');
    } catch (e: any) { message.error(e.message); }
    finally { setBusyUser(null); }
  };
  const filtered = filter === 'all' ? entries : entries.filter(entry => entry.status === filter);
  const delegate = async (entry: AccessEntry) => {
    setBusyUser(entry.user_id);
    try {
      await taskAccess('set-manager', { task_id: taskId, user_id: entry.user_id, enabled: !entry.is_manager });
      setDelegateTarget(null);
      await load();
      message.success(entry.is_manager ? 'ถอนสิทธิ์จัดการแล้ว' : 'ให้สิทธิ์อนุมัติการเข้าร่วมโจทย์แล้ว');
    } catch (e: any) { message.error(e.message); }
    finally { setBusyUser(null); }
  };
  return <div>
    <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
      <Select aria-label="กรองสถานะสิทธิ์" value={filter} onChange={setFilter} style={{ minWidth: 210 }} options={[
        { value: 'all', label: `ทั้งหมด (${entries.length})` },
        ...Object.entries(statusLabels).filter(([value]) => value !== 'rejected').map(([value, label]) => ({ value, label: `${label} (${entries.filter(entry => entry.status === value).length})` })),
      ]} />
      <Space wrap>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
      </Space>
    </Space>
    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>Access manage</Typography.Title>
    {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
    <Table<AccessEntry> rowKey="user_id" dataSource={filtered} loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 620 }} locale={{ emptyText: 'ยังไม่มีรายการสิทธิ์หรือคำขอเข้าถึง' }} columns={[
      { title: 'ผู้ขอสิทธิ์', dataIndex: 'name', width: '45%', render: (name: string, entry) => <>{name || entry.user_id}{!!entry.is_manager && <StarFilled aria-label="ผู้จัดการ task" title="ผู้จัดการ task" style={{ color: '#d48806', marginInlineStart: 8 }} />}</> },
      { title: 'สถานะ', dataIndex: 'status', width: '35%', render: (status: AccessEntry['status']) => <Tag color={statusColors[status]}>{statusLabels[status]}</Tag> },
      {
        title: 'จัดการสิทธิ์', key: 'actions', width: '20%', render: (_, entry) => <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="primary" disabled={!!busyUser || !!entry.protected_access || entry.status !== 'pending'} loading={busyUser === entry.user_id} onClick={() => decide(entry, 'approved')}>อนุมัติ</Button>
          <Button danger disabled={!!busyUser || (!!entry.protected_access && !(canDelegate && entry.is_manager)) || entry.status === 'rejected'} onClick={() => decide(entry, 'rejected')}>{entry.status === 'approved' || entry.is_manager ? 'ถอนสิทธิ์' : 'ปฏิเสธ'}</Button>
          {canDelegate && entry.status === 'approved' && (!entry.protected_access || !!entry.is_manager) && <Dropdown trigger={['click']} menu={{ items: [{ key: 'delegate', label: entry.is_manager ? 'ถอนสิทธิ์อนุมัติการเข้าร่วมโจทย์' : 'ให้สิทธิ์อนุมัติการเข้าร่วมโจทย์', danger: !!entry.is_manager }], onClick: () => setDelegateTarget(entry) }} disabled={!!busyUser}><Button icon={<SettingOutlined />} aria-label="ตั้งค่าสิทธิ์จัดการ" title="ตั้งค่าสิทธิ์จัดการ" disabled={!!busyUser} /></Dropdown>}
        </Space>
      },
    ]} />
    <Modal title={delegateTarget?.is_manager ? 'ยืนยันการถอนสิทธิ์อนุมัติการเข้าร่วมโจทย์แล้ว' : 'ยืนยันการให้สิทธิ์'} open={!!delegateTarget} onCancel={() => !busyUser && setDelegateTarget(null)} closable={!busyUser} maskClosable={!busyUser} keyboard={!busyUser} footer={[
      <Button key="cancel" disabled={!!busyUser} onClick={() => setDelegateTarget(null)}>ยกเลิก</Button>,
      <Button key="confirm" type="primary" danger={!!delegateTarget?.is_manager} loading={!!busyUser} onClick={() => delegateTarget && delegate(delegateTarget)}>ยืนยัน</Button>,
    ]}>
      <p>ผู้ใช้: <strong>{delegateTarget?.name || delegateTarget?.user_id}</strong></p>
      <Alert showIcon type="warning" message={delegateTarget?.is_manager ? <span lang="th" style={{ wordBreak: 'normal', overflowWrap: 'normal', lineHeight: 1.8 }}>
        ผู้ใช้งานท่านนี้จะถูกถอนสิทธิ์ในการ<span style={{ whiteSpace: 'nowrap' }}>อนุมัติ</span> <span style={{ whiteSpace: 'nowrap' }}>ปฏิเสธ</span> และ<span style={{ whiteSpace: 'nowrap' }}>ถอนสิทธิ์</span><span style={{ whiteSpace: 'nowrap' }}>ผู้เข้าร่วมโจทย์</span> โดยยังคง<span style={{ whiteSpace: 'nowrap' }}>สิทธิ์เข้าถึงโจทย์</span>ตามที่ได้รับอนุมัติไว้
      </span> : <span lang="th" style={{ wordBreak: 'normal', overflowWrap: 'normal', lineHeight: 1.8 }}>
        ผู้ใช้งานท่านนี้จะได้รับสิทธิ์เฉพาะ<span style={{ whiteSpace: 'nowrap' }}>อนุมัติ</span> <span style={{ whiteSpace: 'nowrap' }}>ปฏิเสธ</span> และ<span style={{ whiteSpace: 'nowrap' }}>ถอนสิทธิ์</span><span style={{ whiteSpace: 'nowrap' }}>ผู้เข้าร่วมโจทย์</span>เท่านั้น โดยไม่สามารถดำเนินการอื่นนอกเหนือจากสิทธิ์ที่กำหนดได้
      </span>} />
    </Modal>
  </div>;
}
